// Code generated by cmd/gen-tstypes; DO NOT EDIT.
// Source of truth: internal/domain + internal/apitypes (Go).
// Regenerate with `go generate ./...`.

// ---- Value-enums (internal/domain) ----
// Each enum is an `as const` object plus the union type derived from it.

// Debt-type sentinels (single home for the values).
export const DEBT_TYPES = {
	CREDIT_CARD: 'credit_card',
	MORTGAGE: 'mortgage',
	INTEREST_FREE_FACILITY: 'interest_free_facility',
} as const;
export type DebtType = (typeof DEBT_TYPES)[keyof typeof DEBT_TYPES];

// Grouping dimension, orthogonal to debtType.
export const DEBT_CLASSES = {
	CONSUMER: 'consumer',
	ASSET_BACKED: 'asset_backed',
	MONITORED: 'monitored',
} as const;
export type DebtClass = (typeof DEBT_CLASSES)[keyof typeof DEBT_CLASSES];

// What a debt payment cleared.
export const ALLOCATION_KINDS = {
	PURCHASE: 'purchase',
	LEGACY: 'legacy',
	OVERPAYMENT: 'overpayment',
} as const;
export type AllocationKind = (typeof ALLOCATION_KINDS)[keyof typeof ALLOCATION_KINDS];

// Whether a debt payment was the required minimum, extra, or a mix.
export const PAYMENT_ROLES = {
	MINIMUM: 'minimum',
	EXTRA: 'extra',
	MIXED: 'mixed',
} as const;
export type PaymentRole = (typeof PAYMENT_ROLES)[keyof typeof PAYMENT_ROLES];

// Where a debt's repayments come from.
export const PAYMENT_SOURCES = {
	BANK: 'bank',
	PAYROLL: 'payroll',
} as const;
export type PaymentSource = (typeof PAYMENT_SOURCES)[keyof typeof PAYMENT_SOURCES];

// Cadence of an income schedule, breakdown line, or facility repayment.
export const FREQUENCIES = {
	MONTHLY: 'monthly',
	FORTNIGHTLY: 'fortnightly',
	FOUR_WEEKLY: 'four-weekly',
	WEEKLY: 'weekly',
	ONE_OFF: 'one-off',
} as const;
export type Frequency = (typeof FREQUENCIES)[keyof typeof FREQUENCIES];

// Whether an interest-free facility is on track before its deadline.
export const GUARD_STATUSES = {
	OK: 'ok',
	DUE: 'due',
	OVERDUE: 'overdue',
	UNKNOWN: 'unknown',
} as const;
export type GuardStatus = (typeof GUARD_STATUSES)[keyof typeof GUARD_STATUSES];

// How a categorisation rule matches a description.
export const MATCH_TYPES = {
	REGEX: 'regex',
	CONTAINS: 'contains',
} as const;
export type MatchType = (typeof MATCH_TYPES)[keyof typeof MATCH_TYPES];

// Filters a categorisation rule to debits or credits.
export const AMOUNT_SIGNS = {
	POSITIVE: 'positive',
	NEGATIVE: 'negative',
} as const;
export type AmountSign = (typeof AMOUNT_SIGNS)[keyof typeof AMOUNT_SIGNS];

// How a debt's figures are sourced.
export const DATA_SOURCES = {
	FEED: 'feed',
	MANUAL: 'manual',
	HYBRID: 'hybrid',
} as const;
export type DataSource = (typeof DATA_SOURCES)[keyof typeof DATA_SOURCES];

// Reserved category sentinels.
export const UNCAT = 'UNCAT';
export const SPLIT = 'SPLIT';

// ---- API DTOs (internal/apitypes) ----
// JSON shapes returned by / sent to the Go API. Enum-valued fields use the
// union types above. Optional (`?`) tracks Go `omitempty` / pointer fields.

export interface Split {
	category: string;
	amount: number;
	note?: string;
	excludeFromBudget?: boolean;
}

export interface Allocation {
	kind: AllocationKind;
	targetPk?: string;
	targetSk?: string;
	amount: number;
}

export interface Merchant {
	name?: string;
}

export interface TxMeta {
	particulars?: string;
	code?: string;
	reference?: string;
}

export interface Transaction {
	pk: string;
	sk: string;
	txId: string;
	accountId: string;
	date: string;
	description: string;
	amount: number;
	type: string;
	category: string;
	isTransfer: boolean;
	excludeFromBudget: boolean;
	pending: boolean;
	suggestedCategory?: string;
	suggestedConfidence?: number;
	autoCategorised?: boolean;
	monthOffset?: number;
	splits?: Split[];
	debtId?: string;
	debtPaymentRole?: PaymentRole;
	paymentAllocations?: Allocation[];
	metaOtherAccount?: string;
	merchant?: Merchant;
	meta?: TxMeta;
}

export interface Rule {
	pk: string;
	sk: string;
	pattern: string;
	matchField: string;
	matchType: MatchType;
	priority: number;
	amountSign?: AmountSign | '';
	createdAt: string;
}

export interface Category {
	pk: string;
	sk: string;
	name: string;
	type?: string;
	sortOrder: number;
}

export interface IncomeSchedule {
	name?: string;
	category: string;
	amount: number;
	frequency: Frequency;
	monthlyDay?: number;
	fortnightlyPayDate?: string;
	fourWeeklyPayDate?: string;
	oneOffDate?: string;
	weeklyDay?: number;
}

export interface CategoryLine {
	name?: string;
	amount: number;
	frequency: Frequency;
	monthlyDay?: number;
	fortnightlyPayDate?: string;
	fourWeeklyPayDate?: string;
	oneOffDate?: string;
	weeklyDay?: number;
}

export interface Budget {
	pk: string;
	sk: string;
	plannedIncome: number;
	categoryBudgets: Record<string, number>;
	categoryLines?: Record<string, CategoryLine[]>;
	incomeSchedules: IncomeSchedule[];
}

export interface Account {
	pk: string;
	sk: string;
	id: string;
	name: string;
	formattedAccount?: string;
	type?: string;
	balanceCurrent?: number;
	currency?: string;
	connectionName?: string;
	updatedAt?: string;
}

export interface ReconcilePoint {
	date: string;
	balance: number;
	note?: string;
	statementKey?: string;
	statementName?: string;
}

export interface Valuation {
	date: string;
	amount: number;
	note?: string;
}

export interface TrancheBalance {
	accountId: string;
	name: string;
	balance: number;
	updatedAt?: string;
}

export interface UnpaidCharge {
	pk: string;
	sk: string;
	date: string;
	description: string;
	amount: number;
	unpaid: number;
	category: string;
}

export interface SeriesPoint {
	month: string;
	value: number;
}

export interface PaymentView {
	pk: string;
	sk: string;
	date: string;
	description: string;
	amount: number;
	role?: PaymentRole;
	allocations?: Allocation[];
	negatingCurrent: number;
	carriedPaydown: number;
	legacyPaydown: number;
	overpayment: number;
	minimumPortion: number;
	extraPortion: number;
}

export interface Debt {
	pk: string;
	sk: string;
	id: string;
	debtType: DebtType;
	debtClass?: DebtClass;
	name: string;
	cardAccountId?: string;
	cardFormattedAccount?: string;
	legacyBalanceStart?: number;
	trackingStartDate?: string;
	legacyCategory?: string;
	interestCategory?: string;
	legacyRetiredAt?: string;
	dataSource?: DataSource;
	targetBalance?: number;
	originalPrincipal?: number;
	interestRate?: number;
	minimumPayment?: number;
	paymentFrequency?: Frequency;
	startDate?: string;
	endDate?: string;
	penaltyDeadline?: string;
	paymentCategory?: string;
	paymentSource?: PaymentSource;
	mortgageAccountIds?: string[];
	createdAt?: string;
	updatedAt?: string;
}

export interface DebtView extends Debt {
	legacyRemaining: number;
	isLegacyActive: boolean;
	cardCreditsRecognised?: number;
	unpaidChargesTotal?: number;
	unpaidCharges?: UnpaidCharge[];
	payments?: PaymentView[];
	unreconciledPayments?: PaymentView[];
	interestSeries?: SeriesPoint[];
	trajectory?: SeriesPoint[];
	currentBalance?: number;
	expectedBalance?: number;
	aheadBehind?: number;
	guardStatus?: GuardStatus;
	guardDueDate?: string;
	reconciles?: ReconcilePoint[];
	expectedTrajectory?: SeriesPoint[];
	tranches?: TrancheBalance[];
	totalBalance?: number;
	houseValuation?: number;
	valuationAsOf?: string;
	equity?: number;
	equityPct?: number;
	lvrPct?: number;
	valuationHistory?: Valuation[];
	linkedAccountName?: string;
	linkedAccountBalance?: number;
	linkedAccountCurrency?: string;
	linkedAccountBalanceUpdatedAt?: string;
}
